import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Building2, ImagePlus, Loader2, Settings2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { API, fmtError, useUser } from "@/lib/auth";

const inputCls =
  "w-full rounded-xl border border-white/12 bg-[#07090E] px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-emerald-400/60";

export const logoUrl = (user) =>
  user.has_logo ? `${API}/profile/logo?v=${user.logo_version || 0}` : null;

export const BusinessSettings = () => {
  const { user, setUser } = useUser();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: user.name || "",
    company: user.company || "",
  });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) setForm({ name: user.name || "", company: user.company || "" });
  }, [open, user.name, user.company]);

  const bump = (data) =>
    setUser({ ...user, ...data, logo_version: (user.logo_version || 0) + 1 });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/profile`, form);
      bump(data);
      toast.success("İşletme bilgileri güncellendi.");
      setOpen(false);
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/profile/logo`, fd);
      bump(data);
      toast.success("Logo yüklendi — PDF raporlarında kullanılacak.");
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/profile/logo`);
      bump(data);
      toast.success("Logo kaldırıldı.");
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="panel-settings-btn"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 transition-colors duration-300 hover:text-emerald-400"
        >
          <Settings2 className="h-4 w-4" />{" "}
          <span className="hidden sm:inline">Ayarlar</span>
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid="settings-dialog"
        className="border-white/10 bg-[#0D111A] text-slate-50 sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            İşletme Ayarları
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Logon PDF raporlarının başlığında Akış markası yerine görünür.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4 rounded-2xl border border-white/8 bg-[#07090E] p-4">
          <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-[#0D111A]">
            {user.has_logo ? (
              <img
                data-testid="settings-logo-preview"
                src={logoUrl(user)}
                alt="İşletme logosu"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <Building2 className="h-6 w-6 text-slate-600" />
            )}
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <input
              ref={fileRef}
              data-testid="settings-logo-input"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
            <button
              type="button"
              data-testid="settings-logo-upload-btn"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-300 transition-all duration-300 hover:bg-emerald-500/20 disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ImagePlus className="h-3.5 w-3.5" />
              )}
              {user.has_logo ? "Logoyu değiştir" : "Logo yükle"}
            </button>
            {user.has_logo && (
              <button
                type="button"
                data-testid="settings-logo-remove-btn"
                disabled={busy}
                onClick={removeLogo}
                className="inline-flex items-center justify-center gap-1.5 text-xs font-medium text-slate-500 transition-colors duration-300 hover:text-red-400"
              >
                <Trash2 className="h-3.5 w-3.5" /> Kaldır
              </button>
            )}
            <span className="text-[11px] text-slate-600">
              PNG, JPEG veya WebP · en fazla 3 MB
            </span>
          </div>
        </div>

        <form
          data-testid="settings-form"
          onSubmit={save}
          className="mt-2 space-y-3"
        >
          <input
            data-testid="settings-name-input"
            required
            minLength={2}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ad Soyad"
            className={inputCls}
          />
          <input
            data-testid="settings-company-input"
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            placeholder="İşletme adı (raporlarda görünür)"
            className={inputCls}
          />
          <button
            data-testid="settings-save-btn"
            type="submit"
            disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-2.5 text-sm font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
